import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { 
  ArrowLeft, TrendingUp, Wallet, Clock, Shield, Zap, 
  Plus, Minus, RefreshCw, Info, AlertTriangle, Check,
  Coins, PiggyBank, BarChart3, History, Settings
} from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { 
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar
} from "recharts";
import { API } from "../App";
import keyVault from "../crypto/KeyVault";
import { signVaultOperation } from "../crypto/chainSigners";

// Chain configurations
const CHAIN_CONFIG = {
  eth: { name: "Ethereum", color: "#627EEA", icon: "⟠" },
  sol: { name: "Solana", color: "#9945FF", icon: "☀️" },
  xrp: { name: "XRP", color: "#23292F", icon: "🌊" },
  xlm: { name: "Stellar", color: "#14B8E0", icon: "⭐" },
  hbar: { name: "HBAR", color: "#3A3A3A", icon: "⬡" },
  btc: { name: "Bitcoin", color: "#F7931A", icon: "₿" },
  link: { name: "Chainlink", color: "#375BD2", icon: "⬡" }
};

const CHART_COLORS = ["#FFD700", "#00FFFF", "#9945FF", "#14B8E0", "#F7931A", "#627EEA", "#375BD2"];

const YieldVault = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  
  // Data state
  const [vaultStats, setVaultStats] = useState([]);
  const [userPositions, setUserPositions] = useState(null);
  const [settings, setSettings] = useState(null);
  const [apys, setApys] = useState([]);
  const [idleBalances, setIdleBalances] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [nextSweep, setNextSweep] = useState(null);
  const [wallets, setWallets] = useState([]);
  
  // Dialog state
  const [showOptIn, setShowOptIn] = useState(false);
  const [showDeposit, setShowDeposit] = useState(false);
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [showCalculator, setShowCalculator] = useState(false);
  
  // Form state
  const [optInChains, setOptInChains] = useState([]);
  const [optInThreshold, setOptInThreshold] = useState(100);
  const [depositWallet, setDepositWallet] = useState("");
  const [depositAmount, setDepositAmount] = useState("");
  const [withdrawPosition, setWithdrawPosition] = useState("");
  const [withdrawShares, setWithdrawShares] = useState("");
  const [calcChain, setCalcChain] = useState("eth");
  const [calcAmount, setCalcAmount] = useState(1000);
  const [calcResult, setCalcResult] = useState(null);

  const getAuthConfig = () => {
    const token = localStorage.getItem('auth_token');
    return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
  };

  // Fetch all data
  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      const config = getAuthConfig();
      const [
        statsRes, positionsRes, settingsRes, apysRes, 
        idleRes, oppsRes, sweepRes, walletsRes
      ] = await Promise.all([
        axios.get(`${API}/vault/stats`, config).catch(() => ({ data: { vaults: [] } })),
        axios.get(`${API}/vault/user-position`, config).catch(() => ({ data: null })),
        axios.get(`${API}/vault/settings`, config).catch(() => ({ data: null })),
        axios.get(`${API}/vault/apy`, config).catch(() => ({ data: { apys: [] } })),
        axios.get(`${API}/vault/idle-balances`, config).catch(() => ({ data: { idle_balances: [] } })),
        axios.get(`${API}/vault/opportunities`, config).catch(() => ({ data: { opportunities: [] } })),
        axios.get(`${API}/vault/next-sweep`, config).catch(() => ({ data: null })),
        axios.get(`${API}/wallets`, config).catch(() => ({ data: { wallets: [] } }))
      ]);

      setVaultStats(statsRes.data.vaults || []);
      setUserPositions(positionsRes.data);
      setSettings(settingsRes.data);
      setApys(apysRes.data.apys || []);
      setIdleBalances(idleRes.data.idle_balances || []);
      setOpportunities(oppsRes.data.opportunities || []);
      setNextSweep(sweepRes.data);
      setWallets(walletsRes.data.wallets || []);
    } catch (error) {
      console.error("Error fetching vault data:", error);
      toast.error("Failed to load vault data");
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAllData();
    setRefreshing(false);
    toast.success("Data refreshed!");
  };

  const handleOptIn = async () => {
    if (optInChains.length === 0) {
      toast.error("Please select at least one chain");
      return;
    }
    
    try {
      const config = getAuthConfig();
      await axios.post(`${API}/vault/opt-in`, {
        chains: optInChains,
        threshold: optInThreshold,
        auto_compound: true
      }, config);
      
      toast.success("Successfully opted into Yield Vault!");
      setShowOptIn(false);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to opt in");
    }
  };

  const handleOptOut = async () => {
    try {
      const config = getAuthConfig();
      await axios.post(`${API}/vault/opt-out`, {}, config);
      toast.success("Opted out of Yield Vault");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to opt out");
    }
  };

  const handleDeposit = async () => {
    if (!depositWallet || !depositAmount) {
      toast.error("Please fill all fields");
      return;
    }

    // V3: Ensure KeyVault is unlocked
    if (!keyVault.isUnlocked()) {
      toast.error("Please unlock your KeyVault first (go to Dashboard)");
      return;
    }

    // Find the wallet to get chain + address for KeyVault lookup
    const wallet = wallets.find(w => w.wallet_id === depositWallet);
    if (!wallet) {
      toast.error("Wallet not found");
      return;
    }

    const privateKey = keyVault.getKey(wallet.chain, wallet.address);
    if (!privateKey) {
      toast.error("Private key not found in KeyVault. Please re-import this wallet.");
      return;
    }
    
    try {
      const config = getAuthConfig();
      
      // Step 1: Prepare
      toast.info("Preparing vault deposit...");
      const prepareRes = await axios.post(`${API}/v3/vault/deposit/prepare`, {
        wallet_id: depositWallet,
        amount: parseFloat(depositAmount)
      }, config);

      const { tx_blob, chain } = prepareRes.data;

      // Step 2: Sign client-side
      toast.info("Signing deposit transaction...");
      const { signedTx, signature } = await signVaultOperation(tx_blob, privateKey, chain);

      // Step 3: Broadcast
      toast.info("Broadcasting deposit...");
      const broadcastRes = await axios.post(`${API}/v3/vault/deposit/broadcast`, {
        wallet_id: depositWallet,
        chain: chain,
        amount: parseFloat(depositAmount),
        signed_tx: signedTx,
        signature: signature
      }, config);
      
      toast.success(broadcastRes.data.message || "Deposit successful!", {
        description: `Non-custodial • ${broadcastRes.data.shares?.toFixed(4)} shares received`
      });
      setShowDeposit(false);
      setDepositWallet("");
      setDepositAmount("");
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Deposit failed");
    }
  };

  const handleWithdraw = async () => {
    if (!withdrawPosition) {
      toast.error("Please select a position");
      return;
    }

    // V3: Ensure KeyVault is unlocked
    if (!keyVault.isUnlocked()) {
      toast.error("Please unlock your KeyVault first (go to Dashboard)");
      return;
    }

    // Find the position to get wallet info for signing
    const position = positions.find(p => p.position_id === withdrawPosition);
    if (!position) {
      toast.error("Position not found");
      return;
    }

    const wallet = wallets.find(w => w.wallet_id === position.wallet_id);
    if (!wallet) {
      toast.error("Associated wallet not found");
      return;
    }

    const privateKey = keyVault.getKey(wallet.chain, wallet.address);
    if (!privateKey) {
      toast.error("Private key not found in KeyVault. Please re-import this wallet.");
      return;
    }
    
    try {
      const config = getAuthConfig();
      
      // Step 1: Prepare
      toast.info("Preparing vault withdrawal...");
      const prepareRes = await axios.post(`${API}/v3/vault/withdraw/prepare`, {
        position_id: withdrawPosition,
        shares: withdrawShares ? parseFloat(withdrawShares) : null
      }, config);

      const { tx_blob, chain, shares, net_amount, performance_fee } = prepareRes.data;

      // Step 2: Sign client-side
      toast.info("Signing withdrawal transaction...");
      const { signedTx, signature } = await signVaultOperation(tx_blob, privateKey, chain);

      // Step 3: Broadcast
      toast.info("Broadcasting withdrawal...");
      const broadcastRes = await axios.post(`${API}/v3/vault/withdraw/broadcast`, {
        position_id: withdrawPosition,
        chain: chain,
        shares: shares,
        signed_tx: signedTx,
        signature: signature
      }, config);
      
      toast.success(`Withdrawn ${broadcastRes.data.net_amount?.toFixed(4)} tokens!`, {
        description: `Non-custodial • Fee: ${broadcastRes.data.performance_fee?.toFixed(4)}`
      });
      setShowWithdraw(false);
      setWithdrawPosition("");
      setWithdrawShares("");
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Withdrawal failed");
    }
  };

  const handleCalculateYield = async () => {
    try {
      const config = getAuthConfig();
      const res = await axios.post(`${API}/vault/calculate-yield`, {
        chain: calcChain,
        amount: calcAmount,
        days: 365,
        risk_tolerance: "moderate"
      }, config);
      
      setCalcResult(res.data);
    } catch (error) {
      toast.error("Calculation failed");
    }
  };

  const toggleChain = (chain) => {
    setOptInChains(prev => 
      prev.includes(chain) 
        ? prev.filter(c => c !== chain)
        : [...prev, chain]
    );
  };

  // Calculate totals
  const totalTVL = vaultStats.reduce((sum, v) => sum + (v.total_value_locked_usd || 0), 0);
  const avgAPY = apys.length > 0 
    ? apys.reduce((sum, a) => sum + a.net_apy, 0) / apys.length 
    : 0;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#020617]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-2 border-[#FFD700] border-t-transparent rounded-full animate-spin" />
          <p className="text-[#FFD700] font-medium tracking-wider">LOADING VAULT...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#020617] via-[#000A1F] to-[#001133]">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-white/5 bg-[#020617]/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/dashboard')}
                className="text-white/60 hover:text-white"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-3">
                <PiggyBank className="w-8 h-8 text-[#FFD700]" />
                <div>
                  <h1 className="text-xl font-bold text-white font-['Syne']">Yield Vault</h1>
                  <p className="text-white/40 text-xs">Idle Token Sweep System</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleRefresh}
                disabled={refreshing}
                className="text-white/60 hover:text-white"
              >
                <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
              </Button>
              
              {settings?.sweep_enabled ? (
                <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                  <Check className="w-3 h-3 mr-1" />
                  Sweep Active
                </Badge>
              ) : (
                <Button
                  onClick={() => setShowOptIn(true)}
                  className="bg-gradient-to-r from-[#FFD700] to-[#FDB931] text-[#002366] rounded-full"
                >
                  <Zap className="w-4 h-4 mr-2" />
                  Enable Sweep
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Overview Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8"
        >
          {/* Total TVL */}
          <Card className="glass-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-white/50 text-sm">Total Value Locked</span>
                <Wallet className="w-5 h-5 text-[#FFD700]" />
              </div>
              <p className="text-2xl font-bold text-white">
                ${totalTVL.toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </p>
              <p className="text-green-400 text-sm mt-1">Across all vaults</p>
            </CardContent>
          </Card>

          {/* Average APY */}
          <Card className="glass-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-white/50 text-sm">Average APY</span>
                <TrendingUp className="w-5 h-5 text-green-400" />
              </div>
              <p className="text-2xl font-bold text-green-400">
                {avgAPY.toFixed(2)}%
              </p>
              <p className="text-white/40 text-sm mt-1">After fees</p>
            </CardContent>
          </Card>

          {/* Your Positions */}
          <Card className="glass-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-white/50 text-sm">Your Deposits</span>
                <Coins className="w-5 h-5 text-[#00FFFF]" />
              </div>
              <p className="text-2xl font-bold text-white">
                ${userPositions?.total_value?.toLocaleString(undefined, { maximumFractionDigits: 2 }) || '0.00'}
              </p>
              <p className="text-[#FFD700] text-sm mt-1">
                +${userPositions?.total_pending_yield?.toFixed(2) || '0.00'} yield
              </p>
            </CardContent>
          </Card>

          {/* Next Sweep */}
          <Card className="glass-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-white/50 text-sm">Next Sweep</span>
                <Clock className="w-5 h-5 text-purple-400" />
              </div>
              <p className="text-2xl font-bold text-white">
                {nextSweep?.hours_until?.toFixed(1) || '24'}h
              </p>
              <p className="text-white/40 text-sm mt-1">Auto-deposit cycle</p>
            </CardContent>
          </Card>
        </motion.div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-white/5 p-1 rounded-full mb-6">
            <TabsTrigger value="overview" className="tab-quantum">
              <BarChart3 className="w-4 h-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="positions" className="tab-quantum">
              <Wallet className="w-4 h-4 mr-2" />
              My Positions
            </TabsTrigger>
            <TabsTrigger value="opportunities" className="tab-quantum">
              <TrendingUp className="w-4 h-4 mr-2" />
              Opportunities
            </TabsTrigger>
            <TabsTrigger value="settings" className="tab-quantum">
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Vault Cards */}
              <Card className="glass-card lg:col-span-2">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <PiggyBank className="w-5 h-5 text-[#FFD700]" />
                    Active Vaults
                  </CardTitle>
                  <CardDescription className="text-white/50">
                    Deposit your idle tokens to earn yield automatically
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {vaultStats.map((vault, index) => {
                      const chainInfo = CHAIN_CONFIG[vault.chain] || {};
                      return (
                        <motion.div
                          key={vault.chain}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className="p-4 rounded-xl bg-white/5 border border-white/10 hover:border-[#FFD700]/30 transition-colors"
                        >
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <span className="text-2xl">{chainInfo.icon}</span>
                              <span className="text-white font-medium">{chainInfo.name}</span>
                            </div>
                            <Badge 
                              variant="outline" 
                              className="border-green-500/30 text-green-400"
                            >
                              {vault.current_apy?.toFixed(2)}% APY
                            </Badge>
                          </div>
                          
                          <div className="space-y-2">
                            <div className="flex justify-between text-sm">
                              <span className="text-white/50">TVL</span>
                              <span className="text-white">
                                ${vault.total_value_locked_usd?.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                              </span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-white/50">Share Price</span>
                              <span className="text-white">{vault.share_price?.toFixed(4)}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-white/50">Depositors</span>
                              <span className="text-white">{vault.depositor_count?.toLocaleString()}</span>
                            </div>
                          </div>
                          
                          <div className="mt-4 flex gap-2">
                            <Button
                              size="sm"
                              className="flex-1 bg-[#FFD700]/10 text-[#FFD700] hover:bg-[#FFD700]/20"
                              onClick={() => {
                                const wallet = wallets.find(w => w.chain === vault.chain);
                                if (wallet) {
                                  setDepositWallet(wallet.wallet_id);
                                  setShowDeposit(true);
                                } else {
                                  toast.error(`No ${chainInfo.name} wallet found`);
                                }
                              }}
                            >
                              <Plus className="w-3 h-3 mr-1" />
                              Deposit
                            </Button>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* APY Chart */}
              <Card className="glass-card">
                <CardHeader>
                  <CardTitle className="text-white text-sm uppercase tracking-wider">
                    APY by Chain
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={apys}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
                      <XAxis 
                        dataKey="chain" 
                        stroke="#ffffff40"
                        tick={{ fill: '#ffffff60', fontSize: 12 }}
                        tickFormatter={(v) => v.toUpperCase()}
                      />
                      <YAxis 
                        stroke="#ffffff40"
                        tick={{ fill: '#ffffff60', fontSize: 12 }}
                        tickFormatter={(v) => `${v}%`}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#0B1221',
                          border: '1px solid #FFD70030',
                          borderRadius: '8px'
                        }}
                        formatter={(value) => [`${value.toFixed(2)}%`, 'Net APY']}
                      />
                      <Bar dataKey="net_apy" fill="#FFD700" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Yield Sources Distribution */}
              <Card className="glass-card">
                <CardHeader>
                  <CardTitle className="text-white text-sm uppercase tracking-wider">
                    Yield Sources
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {vaultStats[0]?.yield_sources?.map((source, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: CHART_COLORS[idx % CHART_COLORS.length] }}
                          />
                          <span className="text-white text-sm">{source.name}</span>
                        </div>
                        <div className="flex items-center gap-4">
                          <Badge variant="outline" className="text-white/60">
                            {source.allocation}%
                          </Badge>
                          <span className="text-green-400 font-medium">
                            {source.apy?.toFixed(2)}% APY
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Positions Tab */}
          <TabsContent value="positions">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Position Summary */}
              <Card className="glass-card lg:col-span-2">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-white">Your Vault Positions</CardTitle>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        className="btn-gold"
                        onClick={() => setShowDeposit(true)}
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Deposit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="btn-outline-gold"
                        onClick={() => setShowWithdraw(true)}
                        disabled={!userPositions?.positions?.length}
                      >
                        <Minus className="w-4 h-4 mr-1" />
                        Withdraw
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {userPositions?.positions?.length > 0 ? (
                    <div className="space-y-4">
                      {userPositions.positions.map((pos, idx) => {
                        const chainInfo = CHAIN_CONFIG[pos.chain] || {};
                        const canWithdraw = new Date(pos.lock_until) <= new Date();
                        const yieldPercent = pos.deposited_amount > 0 
                          ? ((pos.current_value - pos.deposited_amount) / pos.deposited_amount * 100)
                          : 0;
                        
                        return (
                          <motion.div
                            key={pos.position_id}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: idx * 0.1 }}
                            className="p-4 rounded-xl bg-white/5 border border-white/10"
                          >
                            <div className="flex items-center justify-between mb-4">
                              <div className="flex items-center gap-3">
                                <span className="text-2xl">{chainInfo.icon}</span>
                                <div>
                                  <p className="text-white font-medium">{chainInfo.name} Vault</p>
                                  <p className="text-white/40 text-xs">{pos.position_id}</p>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="text-white font-bold">
                                  {pos.current_value?.toFixed(4)} {pos.chain.toUpperCase()}
                                </p>
                                <p className={`text-sm ${yieldPercent >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                  {yieldPercent >= 0 ? '+' : ''}{yieldPercent.toFixed(2)}%
                                </p>
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-4 gap-4 mb-4">
                              <div>
                                <p className="text-white/40 text-xs">Deposited</p>
                                <p className="text-white">{pos.deposited_amount?.toFixed(4)}</p>
                              </div>
                              <div>
                                <p className="text-white/40 text-xs">Shares</p>
                                <p className="text-white">{pos.shares?.toFixed(4)}</p>
                              </div>
                              <div>
                                <p className="text-white/40 text-xs">Pending Yield</p>
                                <p className="text-[#FFD700]">+{pos.pending_yield?.toFixed(4)}</p>
                              </div>
                              <div>
                                <p className="text-white/40 text-xs">Fees Paid</p>
                                <p className="text-white/60">{pos.fees_paid?.toFixed(4)}</p>
                              </div>
                            </div>
                            
                            <div className="flex items-center justify-between">
                              {canWithdraw ? (
                                <Badge className="bg-green-500/20 text-green-400">
                                  <Check className="w-3 h-3 mr-1" />
                                  Unlocked
                                </Badge>
                              ) : (
                                <Badge className="bg-yellow-500/20 text-yellow-400">
                                  <Clock className="w-3 h-3 mr-1" />
                                  Locked until {new Date(pos.lock_until).toLocaleDateString()}
                                </Badge>
                              )}
                              
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-white/20 text-white"
                                disabled={!canWithdraw}
                                onClick={() => {
                                  setWithdrawPosition(pos.position_id);
                                  setShowWithdraw(true);
                                }}
                              >
                                Withdraw
                              </Button>
                            </div>
                          </motion.div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <PiggyBank className="w-16 h-16 text-white/20 mx-auto mb-4" />
                      <p className="text-white/60 mb-4">No vault positions yet</p>
                      <Button
                        onClick={() => setShowDeposit(true)}
                        className="btn-gold"
                      >
                        Make Your First Deposit
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Yield Calculator */}
              <Card className="glass-card">
                <CardHeader>
                  <CardTitle className="text-white text-sm uppercase tracking-wider">
                    Yield Calculator
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label className="text-white/60">Select Chain</Label>
                    <Select value={calcChain} onValueChange={setCalcChain}>
                      <SelectTrigger className="bg-white/5 border-white/10 text-white mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0B1221] border-white/10">
                        {Object.entries(CHAIN_CONFIG).map(([key, config]) => (
                          <SelectItem key={key} value={key} className="text-white">
                            {config.icon} {config.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label className="text-white/60">Deposit Amount</Label>
                    <Input
                      type="number"
                      value={calcAmount}
                      onChange={(e) => setCalcAmount(parseFloat(e.target.value) || 0)}
                      className="bg-white/5 border-white/10 text-white mt-1"
                    />
                  </div>
                  
                  <Button
                    onClick={handleCalculateYield}
                    className="w-full btn-gold"
                  >
                    Calculate
                  </Button>
                  
                  {calcResult && (
                    <div className="mt-4 p-4 rounded-lg bg-white/5 space-y-2">
                      <div className="flex justify-between">
                        <span className="text-white/50">Principal</span>
                        <span className="text-white">{calcResult.principal}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-white/50">Gross Yield (1yr)</span>
                        <span className="text-green-400">+{calcResult.gross_yield?.toFixed(4)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-white/50">Performance Fee</span>
                        <span className="text-white/60">-{calcResult.fee_amount?.toFixed(4)}</span>
                      </div>
                      <div className="border-t border-white/10 pt-2 flex justify-between">
                        <span className="text-white font-medium">Net Yield</span>
                        <span className="text-[#FFD700] font-bold">+{calcResult.net_yield?.toFixed(4)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-white/50">Net APY</span>
                        <span className="text-green-400">{calcResult.net_apy?.toFixed(2)}%</span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Opportunities Tab */}
          <TabsContent value="opportunities">
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="text-white">Yield Opportunities</CardTitle>
                <CardDescription className="text-white/50">
                  Available yield sources across all chains
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left text-white/50 text-sm py-3 px-4">Protocol</th>
                        <th className="text-left text-white/50 text-sm py-3 px-4">Chain</th>
                        <th className="text-right text-white/50 text-sm py-3 px-4">APY</th>
                        <th className="text-right text-white/50 text-sm py-3 px-4">TVL</th>
                        <th className="text-center text-white/50 text-sm py-3 px-4">Risk</th>
                        <th className="text-right text-white/50 text-sm py-3 px-4">Min Deposit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {opportunities.map((opp, idx) => {
                        const chainInfo = CHAIN_CONFIG[opp.chain] || {};
                        return (
                          <tr key={idx} className="border-b border-white/5 hover:bg-white/5">
                            <td className="py-3 px-4">
                              <span className="text-white">{opp.name}</span>
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-2">
                                <span>{chainInfo.icon}</span>
                                <span className="text-white/60">{chainInfo.name}</span>
                              </div>
                            </td>
                            <td className="py-3 px-4 text-right">
                              <span className="text-green-400 font-medium">{opp.apy?.toFixed(2)}%</span>
                            </td>
                            <td className="py-3 px-4 text-right">
                              <span className="text-white">${opp.tvl?.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <Badge 
                                variant="outline"
                                className={
                                  opp.risk_level === 'low' 
                                    ? 'border-green-500/30 text-green-400'
                                    : opp.risk_level === 'medium'
                                    ? 'border-yellow-500/30 text-yellow-400'
                                    : 'border-red-500/30 text-red-400'
                                }
                              >
                                {opp.risk_level}
                              </Badge>
                            </td>
                            <td className="py-3 px-4 text-right">
                              <span className="text-white/60">{opp.min_deposit}</span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Sweep Settings */}
              <Card className="glass-card">
                <CardHeader>
                  <CardTitle className="text-white">Sweep Settings</CardTitle>
                  <CardDescription className="text-white/50">
                    Configure automatic idle token detection
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-center justify-between p-4 rounded-lg bg-white/5">
                    <div>
                      <p className="text-white font-medium">Auto-Sweep</p>
                      <p className="text-white/50 text-sm">Automatically deposit idle tokens</p>
                    </div>
                    <Switch
                      checked={settings?.sweep_enabled || false}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setShowOptIn(true);
                        } else {
                          handleOptOut();
                        }
                      }}
                    />
                  </div>
                  
                  {settings?.sweep_enabled && (
                    <>
                      <div>
                        <Label className="text-white/60">Sweep Threshold</Label>
                        <p className="text-white mt-1">${settings.sweep_threshold} minimum</p>
                      </div>
                      
                      <div>
                        <Label className="text-white/60">Enabled Chains</Label>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {settings.enabled_chains?.map(chain => {
                            const info = CHAIN_CONFIG[chain];
                            return (
                              <Badge key={chain} className="bg-[#FFD700]/20 text-[#FFD700]">
                                {info?.icon} {info?.name}
                              </Badge>
                            );
                          })}
                        </div>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Idle Balances */}
              <Card className="glass-card">
                <CardHeader>
                  <CardTitle className="text-white">Idle Balances</CardTitle>
                  <CardDescription className="text-white/50">
                    Balances eligible for next sweep
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {idleBalances.length > 0 ? (
                    <div className="space-y-3">
                      {idleBalances.map((bal, idx) => {
                        const chainInfo = CHAIN_CONFIG[bal.chain] || {};
                        return (
                          <div 
                            key={idx}
                            className="flex items-center justify-between p-3 rounded-lg bg-white/5"
                          >
                            <div className="flex items-center gap-3">
                              <span className="text-xl">{chainInfo.icon}</span>
                              <div>
                                <p className="text-white">{chainInfo.name}</p>
                                <p className="text-white/40 text-xs">
                                  Idle for {bal.hours_idle}h
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-white">{bal.balance?.toFixed(4)}</p>
                              {bal.eligible_for_sweep && (
                                <Badge className="bg-green-500/20 text-green-400 text-xs">
                                  Ready for sweep
                                </Badge>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-white/50 text-center py-8">
                      No idle balances detected
                    </p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Opt-In Dialog */}
      <Dialog open={showOptIn} onOpenChange={setShowOptIn}>
        <DialogContent className="glass-card border-[#FFD700]/20 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white font-['Syne']">Enable Yield Sweep</DialogTitle>
            <DialogDescription className="text-white/50">
              Automatically deposit idle tokens into yield vaults
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 mt-4">
            <div>
              <Label className="text-white/70">Select Chains to Sweep</Label>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {Object.entries(CHAIN_CONFIG).map(([key, config]) => (
                  <button
                    key={key}
                    onClick={() => toggleChain(key)}
                    className={`p-3 rounded-lg border transition-colors ${
                      optInChains.includes(key)
                        ? 'border-[#FFD700] bg-[#FFD700]/10'
                        : 'border-white/10 hover:border-white/30'
                    }`}
                  >
                    <span className="text-xl">{config.icon}</span>
                    <p className="text-white text-xs mt-1">{config.name}</p>
                  </button>
                ))}
              </div>
            </div>
            
            <div>
              <Label className="text-white/70">Minimum Balance Threshold</Label>
              <Input
                type="number"
                value={optInThreshold}
                onChange={(e) => setOptInThreshold(parseFloat(e.target.value) || 0)}
                className="bg-white/5 border-white/10 text-white mt-1"
                placeholder="100"
              />
              <p className="text-white/40 text-xs mt-1">
                Only sweep balances above this USD value
              </p>
            </div>
            
            <div className="p-4 rounded-lg bg-[#00FFFF]/10 border border-[#00FFFF]/30">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-[#00FFFF] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-[#00FFFF] font-medium">Non-Custodial (v3)</p>
                  <p className="text-white/60 text-sm">
                    Your private keys never leave your browser. Transactions are
                    signed client-side and verified on-chain. Xavier never holds your keys.
                  </p>
                </div>
              </div>
            </div>
            
            <Button onClick={handleOptIn} className="w-full btn-gold">
              <Zap className="w-4 h-4 mr-2" />
              Enable Sweep
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Deposit Dialog */}
      <Dialog open={showDeposit} onOpenChange={setShowDeposit}>
        <DialogContent className="glass-card border-[#FFD700]/20">
          <DialogHeader>
            <DialogTitle className="text-white font-['Syne']">Deposit to Vault</DialogTitle>
            <DialogDescription className="text-white/50">
              Deposit tokens to start earning yield
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-white/70">Select Wallet</Label>
              <Select value={depositWallet} onValueChange={setDepositWallet}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white mt-1">
                  <SelectValue placeholder="Select wallet" />
                </SelectTrigger>
                <SelectContent className="bg-[#0B1221] border-white/10">
                  {wallets.map((wallet) => {
                    const info = CHAIN_CONFIG[wallet.chain] || {};
                    return (
                      <SelectItem 
                        key={wallet.wallet_id} 
                        value={wallet.wallet_id}
                        className="text-white"
                      >
                        {info.icon} {wallet.name} ({wallet.balance?.toFixed(4)} {wallet.chain.toUpperCase()})
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-white/70">Amount</Label>
              <Input
                type="number"
                value={depositAmount}
                onChange={(e) => setDepositAmount(e.target.value)}
                className="bg-white/5 border-white/10 text-white mt-1"
                placeholder="0.00"
              />
            </div>
            
            <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-yellow-400" />
                <span className="text-yellow-400 text-sm">24-hour lock period applies</span>
              </div>
            </div>
            
            <Button onClick={handleDeposit} className="w-full btn-gold">
              <Plus className="w-4 h-4 mr-2" />
              Deposit
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Withdraw Dialog */}
      <Dialog open={showWithdraw} onOpenChange={setShowWithdraw}>
        <DialogContent className="glass-card border-[#FFD700]/20">
          <DialogHeader>
            <DialogTitle className="text-white font-['Syne']">Withdraw from Vault</DialogTitle>
            <DialogDescription className="text-white/50">
              Withdraw your tokens plus earned yield
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-white/70">Select Position</Label>
              <Select value={withdrawPosition} onValueChange={setWithdrawPosition}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white mt-1">
                  <SelectValue placeholder="Select position" />
                </SelectTrigger>
                <SelectContent className="bg-[#0B1221] border-white/10">
                  {userPositions?.positions?.filter(p => p.status === 'active').map((pos) => {
                    const info = CHAIN_CONFIG[pos.chain] || {};
                    return (
                      <SelectItem 
                        key={pos.position_id} 
                        value={pos.position_id}
                        className="text-white"
                      >
                        {info.icon} {pos.shares?.toFixed(4)} shares ({pos.current_value?.toFixed(4)} {pos.chain.toUpperCase()})
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-white/70">Shares to Withdraw (leave empty for all)</Label>
              <Input
                type="number"
                value={withdrawShares}
                onChange={(e) => setWithdrawShares(e.target.value)}
                className="bg-white/5 border-white/10 text-white mt-1"
                placeholder="All shares"
              />
            </div>
            
            <div className="p-3 rounded-lg bg-white/5">
              <p className="text-white/50 text-sm">
                A 15% performance fee will be deducted from your yield earnings.
              </p>
            </div>
            
            <Button onClick={handleWithdraw} className="w-full btn-gold">
              <Minus className="w-4 h-4 mr-2" />
              Withdraw
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default YieldVault;
